// src/services/appRegistryService.ts
import { firestoreWrapper } from './firestoreWrapper';
import { serverTimestamp } from 'firebase/firestore';
import type {
  AppRegistryEntry,
  AppSubmission,
  AppStatus,
  AppVisibility,
} from '../types/appRegistry';

const APPS_COLLECTION = 'apps';

/**
 * Get all approved apps.
 * All approved apps are now visible to every authenticated user.
 * Users control their own dashboard via the hidden-apps feature.
 */
export async function getApprovedAppsForUser(
  _userEmail: string,
  _isAdmin: boolean
): Promise<AppRegistryEntry[]> {
  const colRef = firestoreWrapper.collection(APPS_COLLECTION);
  const q = firestoreWrapper.query(
    colRef,
    firestoreWrapper.where('status', '==', 'approved')
  );
  const snapshot = await firestoreWrapper.getDocs(q);
  return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() } as AppRegistryEntry));
}

/**
 * Submit a new external app for approval.
 * Returns the new document ID.
 */
export async function submitApp(
  submission: AppSubmission,
  submitterEmail: string
): Promise<string> {
  const colRef = firestoreWrapper.collection(APPS_COLLECTION);

  const newApp: Omit<AppRegistryEntry, 'id'> = {
    name: submission.name,
    description: submission.description,
    url: submission.url,
    type: 'external',
    category: submission.category,
    status: 'pending',
    faviconUrl: submission.faviconUrl,
    screenshotUrl: submission.screenshotUrl,
    iconColor: submission.iconColor,
    customIconUrl: submission.customIconUrl,
    ogTitle: null,
    ogDescription: null,
    ogImage: null,
    submittedBy: submitterEmail.toLowerCase(),
    submittedAt: serverTimestamp() as any,
    approvedBy: null,
    approvedAt: null,
    adminNotes: null,
    visibility: 'global',
    allowedUsers: [],
    globallyHidden: false,
    metricsEnabled: false,
    metricsConfig: null,
  };

  const docRef = await firestoreWrapper.addDoc(colRef as any, newApp as any);
  return docRef.id;
}

/**
 * Update an app's status (admin action).
 */
export async function updateAppStatus(
  appId: string,
  status: AppStatus,
  adminEmail: string,
  options?: {
    visibility?: AppVisibility;
    allowedUsers?: string[];
    adminNotes?: string;
  }
): Promise<void> {
  const colRef = firestoreWrapper.collection(APPS_COLLECTION);
  const docRef = firestoreWrapper.doc(colRef, appId);

  const update: Record<string, unknown> = {
    status,
    approvedBy: adminEmail,
    approvedAt: serverTimestamp(),
  };

  if (options?.visibility !== undefined) {
    update.visibility = options.visibility;
  }
  if (options?.allowedUsers !== undefined) {
    update.allowedUsers = options.allowedUsers;
  }
  if (options?.adminNotes !== undefined) {
    update.adminNotes = options.adminNotes;
  }

  await firestoreWrapper.updateDoc(docRef as any, update);
}

/**
 * Update any fields on an app document (admin editing).
 */
export async function updateApp(
  appId: string,
  fields: Partial<Omit<AppRegistryEntry, 'id'>>
): Promise<void> {
  const colRef = firestoreWrapper.collection(APPS_COLLECTION);
  const docRef = firestoreWrapper.doc(colRef, appId);
  await firestoreWrapper.updateDoc(docRef as any, fields as any);
}

/**
 * Get apps filtered by status (for admin panel).
 */
export async function getAppsByStatus(status: AppStatus): Promise<AppRegistryEntry[]> {
  const colRef = firestoreWrapper.collection(APPS_COLLECTION);
  const q = firestoreWrapper.query(
    colRef,
    firestoreWrapper.where('status', '==', status)
  );
  const snapshot = await firestoreWrapper.getDocs(q);
  return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() } as AppRegistryEntry));
}

/**
 * Get all apps submitted by a specific user (for My Submissions view).
 */
export async function getAppsBySubmitter(email: string): Promise<AppRegistryEntry[]> {
  const colRef = firestoreWrapper.collection(APPS_COLLECTION);
  const q = firestoreWrapper.query(
    colRef,
    firestoreWrapper.where('submittedBy', '==', email.toLowerCase())
  );
  const snapshot = await firestoreWrapper.getDocs(q);
  return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() } as AppRegistryEntry));
}

/**
 * Get a single app by ID.
 */
export async function getAppById(appId: string): Promise<AppRegistryEntry | null> {
  const colRef = firestoreWrapper.collection(APPS_COLLECTION);
  const docRef = firestoreWrapper.doc(colRef, appId);
  const snap = await firestoreWrapper.getDoc(docRef);
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() } as AppRegistryEntry;
}

/**
 * Delete an app (admin only).
 */
export async function deleteApp(appId: string): Promise<void> {
  const colRef = firestoreWrapper.collection(APPS_COLLECTION);
  const docRef = firestoreWrapper.doc(colRef, appId);
  await firestoreWrapper.deleteDoc(docRef as any);
}

/**
 * @deprecated No longer used after hidden-apps migration.
 */
export async function getSpecificVisibilityApps(): Promise<AppRegistryEntry[]> {
  return [];
}

/**
 * Get all apps regardless of status (for admin editing).
 */
export async function getAllApps(): Promise<AppRegistryEntry[]> {
  const colRef = firestoreWrapper.collection(APPS_COLLECTION);
  const snapshot = await firestoreWrapper.getDocs(colRef);
  return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() } as AppRegistryEntry));
}

/**
 * Get all approved apps (for badge rendering in Manage Users).
 */
export async function getAllApprovedApps(): Promise<AppRegistryEntry[]> {
  const colRef = firestoreWrapper.collection(APPS_COLLECTION);
  const q = firestoreWrapper.query(
    colRef,
    firestoreWrapper.where('status', '==', 'approved')
  );
  const snapshot = await firestoreWrapper.getDocs(q);
  return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() } as AppRegistryEntry));
}
